import { supabase } from '@/integrations/supabase/client';

export interface RagScheduledTask {
  id: string;
  task_type: string;
  region: string;
  status: 'active' | 'paused' | 'failed';
  schedule_time: string;
  next_run: string | null;
  config: {
    photo_url?: string;
    day_of_week?: number;
  };
}

export class RagSchedulingService {
  /**
   * Hent scheduled tasks for alle RAG-regioner
   */
  static async getAllRegionTasks(): Promise<Record<string, RagScheduledTask>> {
    const { data, error } = await supabase
      .from('scheduled_tasks')
      .select('*')
      .not('region', 'is', null)
      .order('region');

    if (error) {
      console.error('[RagScheduling] Error fetching tasks:', error);
      return {};
    }

    // Map til region -> task
    const taskMap: Record<string, RagScheduledTask> = {};
    data?.forEach((task: any) => {
      if (task.region) {
        taskMap[task.region] = task;
      }
    });

    return taskMap;
  }

  /**
   * Toggle status for en spesifikk region (active <-> paused)
   */
  static async toggleRegionStatus(region: string, enable: boolean): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('scheduled_tasks')
        .update({ status: enable ? 'active' : 'paused' })
        .eq('region', region);

      if (error) {
        console.error(`[RagScheduling] Error toggling ${region}:`, error);
        return false;
      }

      console.log(`[RagScheduling] ✅ ${region} set to ${enable ? 'active' : 'paused'}`);
      return true;
    } catch (error) {
      console.error(`[RagScheduling] Exception toggling ${region}:`, error);
      return false;
    }
  }

  /**
   * Formater next_run til lesbart format
   */
  static formatNextRun(nextRun: string | null): string {
    if (!nextRun) return 'Ikke planlagt';
    
    const date = new Date(nextRun);
    const now = new Date();
    
    // Norsk dato/tid format
    const dateStr = date.toLocaleDateString('nb-NO', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short' 
    });
    const timeStr = date.toLocaleTimeString('nb-NO', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
    
    // Vis hvor langt unna
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${dateStr} kl ${timeStr} (om ${diffDays}d ${diffHours}t)`;
    } else if (diffHours > 0) {
      return `${dateStr} kl ${timeStr} (om ${diffHours}t)`;
    } else {
      return `${dateStr} kl ${timeStr} (snart)`;
    }
  }
}
